// src/pages/machines/MachineList.jsx
// Site Machine Registry — lists all installed machines per site
// Collection: site_machines
// Multi-tenant safe: company_id on every query

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection, getDocs, query, where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { formatMachineCapacity } from "../../utils/machineCapacity";

const getUser = () => JSON.parse(localStorage.getItem("user") || "{}");

const STATUS_CONFIG = {
  working:      { label: "Working",      bg: "#dcfce7", color: "#166534", dot: "#22c55e" },
  under_repair: { label: "Under Repair", bg: "#fef9c3", color: "#854d0e", dot: "#eab308" },
  breakdown:    { label: "Breakdown",    bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" },
  inactive:     { label: "Inactive",     bg: "#f1f5f9", color: "#475569", dot: "#94a3b8" },
};

// "VRF" is stored as-is; the dropdown shows it as "VRF / VRV" because VRV is
// Daikin's brand name for the same technology — clients use both words.
const MACHINE_TYPES = ["VRF", "Split AC", "Cassette AC", "Chiller", "AHU", "FCU", "Ducted AC", "Other"];

export default function MachineList() {
  const navigate   = useNavigate();
  const me         = getUser();
  const companyId  = me.company_id || "";

  const [machines,    setMachines]    = useState([]);
  const [clients,     setClients]     = useState([]);
  const [sites,       setSites]       = useState([]);
  const [loading,     setLoading]     = useState(true);

  // Filters
  const [selClient,   setSelClient]   = useState("");
  const [selSite,     setSelSite]     = useState("");
  const [selStatus,   setSelStatus]   = useState("all");
  const [selType,     setSelType]     = useState("all");
  const [search,      setSearch]      = useState("");

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    setLoading(true);
    try {
      const [mSnap, cSnap, sSnap] = await Promise.all([
        getDocs(query(collection(db, "site_machines"), where("company_id", "==", companyId))),
        getDocs(query(collection(db, "clients"),       where("company_id", "==", companyId))),
        getDocs(query(collection(db, "sites"),         where("company_id", "==", companyId))),
      ]);
      let machinesData = mSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      let clientsData  = cSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      let sitesData     = sSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Technician scoping — consistent with the "My Projects" pattern.
      // Technicians only see machines at sites where they have (or had) an
      // assigned service job — not the full company-wide machine registry.
      if (me.role === "technician") {
        const techUid = me.uid || me.id;
        const taskSnap = await getDocs(query(
          collection(db, "service_tasks"),
          where("company_id", "==", companyId),
          where("assigned_tech_ids", "array-contains", techUid),
        ));
        const allowedSiteIds = new Set(
          taskSnap.docs.map(d => d.data().site_id).filter(Boolean)
        );
        machinesData = machinesData.filter(m => allowedSiteIds.has(m.site_id));
        sitesData     = sitesData.filter(s => allowedSiteIds.has(s.id));
        const allowedClientIds = new Set(sitesData.map(s => s.client_id).filter(Boolean));
        clientsData  = clientsData.filter(c => allowedClientIds.has(c.id));
      }

      setMachines(machinesData);
      setClients(clientsData);
      setSites(sitesData);
    } catch (err) {
      console.error("MachineList loadAll error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Filtered sites based on selected client
  const filteredSites = selClient
    ? sites.filter(s => s.client_id === selClient)
    : sites;

  // Apply all filters
  const filtered = machines.filter(m => {
    if (selClient && m.client_id !== selClient) return false;
    if (selSite   && m.site_id   !== selSite)   return false;
    if (selStatus !== "all" && m.current_status !== selStatus) return false;
    if (selType   !== "all" && m.machine_type   !== selType)   return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (m.machine_name  || "").toLowerCase().includes(q) ||
        (m.brand         || "").toLowerCase().includes(q) ||
        (m.model_no      || "").toLowerCase().includes(q) ||
        (m.serial_no     || "").toLowerCase().includes(q) ||
        (m.site_name     || "").toLowerCase().includes(q) ||
        (m.client_name   || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Summary counts
  const counts = {
    total:        machines.length,
    working:      machines.filter(m => m.current_status === "working").length,
    under_repair: machines.filter(m => m.current_status === "under_repair").length,
    breakdown:    machines.filter(m => m.current_status === "breakdown").length,
  };

  return (
    <div style={{ padding: "20px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "system-ui, sans-serif" }}>

      {/* ── HEADER ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{
            width: "42px", height: "42px", borderRadius: "12px",
            background: "linear-gradient(135deg, #0ea5e9, #6366f1)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "20px", boxShadow: "0 4px 12px rgba(14,165,233,0.3)",
          }}>🏭</div>
          <div>
            <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "800", color: "#0f172a" }}>Site Machines</h2>
            <p style={{ margin: "2px 0 0", color: "#94a3b8", fontSize: "13px" }}>Installed AC / VRF units across all sites</p>
          </div>
        </div>
        {me.role !== "technician" && (
          <button
            onClick={() => navigate("/machines/add")}
            style={{
              background: "linear-gradient(135deg, #0ea5e9, #6366f1)",
              color: "#fff", border: "none", padding: "10px 20px",
              borderRadius: "12px", fontWeight: "700", fontSize: "14px",
              cursor: "pointer", boxShadow: "0 4px 12px rgba(99,102,241,0.3)",
            }}>
            + Add Machine
          </button>
        )}
      </div>

      {/* ── SUMMARY CARDS ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "12px", marginBottom: "20px" }}>
        {[
          { label: "Total",        value: counts.total,        bg: "#eff6ff", color: "#1d4ed8", icon: "🏭" },
          { label: "Working",      value: counts.working,      bg: "#f0fdf4", color: "#166534", icon: "✅" },
          { label: "Under Repair", value: counts.under_repair, bg: "#fefce8", color: "#854d0e", icon: "🔧" },
          { label: "Breakdown",    value: counts.breakdown,    bg: "#fef2f2", color: "#991b1b", icon: "🚨" },
        ].map(c => (
          <div key={c.label} style={{
            background: c.bg, borderRadius: "14px", padding: "16px",
            border: `1.5px solid ${c.bg}`,
            boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
          }}>
            <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px" }}>
              {c.icon} {c.label}
            </div>
            <div style={{ fontSize: "28px", fontWeight: "800", color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* ── FILTERS ── */}
      <div style={{
        background: "#fff", borderRadius: "14px", padding: "16px",
        marginBottom: "16px", border: "1px solid #e2e8f0",
        display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center",
      }}>
        <input
          placeholder="🔎 Search machine, brand, serial, site..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            flex: "1", minWidth: "200px", padding: "9px 14px",
            border: "1.5px solid #e2e8f0", borderRadius: "10px",
            fontSize: "13px", outline: "none", background: "#f8fafc",
          }}
        />
        <select value={selClient} onChange={e => { setSelClient(e.target.value); setSelSite(""); }}
          style={{ padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: "10px", fontSize: "13px", background: "#f8fafc", outline: "none" }}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
        </select>
        <select value={selSite} onChange={e => setSelSite(e.target.value)}
          style={{ padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: "10px", fontSize: "13px", background: "#f8fafc", outline: "none" }}>
          <option value="">All Sites</option>
          {filteredSites.map(s => <option key={s.id} value={s.id}>{s.site_name}</option>)}
        </select>
        <select value={selStatus} onChange={e => setSelStatus(e.target.value)}
          style={{ padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: "10px", fontSize: "13px", background: "#f8fafc", outline: "none" }}>
          <option value="all">All Status</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={selType} onChange={e => setSelType(e.target.value)}
          style={{ padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: "10px", fontSize: "13px", background: "#f8fafc", outline: "none" }}>
          <option value="all">All Types</option>
          {MACHINE_TYPES.map(t => <option key={t} value={t}>{t === "VRF" ? "VRF / VRV" : t}</option>)}
        </select>
        {(selClient || selSite || selStatus !== "all" || selType !== "all" || search) && (
          <button onClick={() => { setSelClient(""); setSelSite(""); setSelStatus("all"); setSelType("all"); setSearch(""); }}
            style={{ padding: "9px 14px", borderRadius: "10px", border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: "13px", cursor: "pointer" }}>
            ✕ Clear
          </button>
        )}
      </div>

      {/* ── LIST ── */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px", color: "#94a3b8" }}>
          <div style={{ fontSize: "32px", marginBottom: "10px" }}>⏳</div>
          <p>Loading machines...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px", background: "#fff", borderRadius: "16px", border: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: "40px", marginBottom: "12px" }}>🏭</div>
          <p style={{ color: "#64748b", fontWeight: "600" }}>No machines found</p>
          <p style={{ color: "#94a3b8", fontSize: "13px", marginTop: "4px" }}>
            {me.role === "technician"
              ? "You'll see machines here once you're assigned to a service job at a site with registered machines."
              : "Add machines installed at client sites"}
          </p>
          {me.role !== "technician" && (
            <button onClick={() => navigate("/machines/add")}
              style={{ marginTop: "16px", padding: "10px 24px", background: "#6366f1", color: "#fff", border: "none", borderRadius: "10px", fontWeight: "700", cursor: "pointer" }}>
              + Add First Machine
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "14px" }}>
          {filtered.map(m => {
            const st = STATUS_CONFIG[m.current_status] || STATUS_CONFIG.working;
            return (
              <div
                key={m.id}
                onClick={() => navigate(`/machines/${m.id}`)}
                style={{
                  background: "#fff", borderRadius: "16px", padding: "18px",
                  border: "1px solid #e2e8f0", cursor: "pointer",
                  borderLeft: `4px solid ${st.dot}`,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                  transition: "box-shadow 0.15s, transform 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.1)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.05)"; e.currentTarget.style.transform = "none"; }}
              >
                {/* Top row */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontWeight: "700", fontSize: "15px", color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {m.machine_name || "Unnamed Machine"}
                    </p>
                    <p style={{ margin: "3px 0 0", fontSize: "12px", color: "#64748b" }}>
                      {m.brand || "—"} {m.model_no ? `• ${m.model_no}` : ""}
                    </p>
                  </div>
                  <span style={{
                    background: st.bg, color: st.color, fontSize: "11px", fontWeight: "700",
                    padding: "3px 10px", borderRadius: "999px", flexShrink: 0, marginLeft: "8px",
                  }}>
                    {st.label}
                  </span>
                </div>

                {/* Info grid */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "12px" }}>
                  <InfoPill icon="🏷️" label="Type"     value={m.machine_type || "—"} />
                  <InfoPill icon="❄️" label="Capacity" value={formatMachineCapacity(m)} />
                  <InfoPill icon="🔢" label="Serial"   value={m.serial_no || "—"} />
                  <InfoPill icon="📅" label="Installed" value={m.installation_date ? new Date(m.installation_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"} />
                </div>

                {/* Site + Client */}
                <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: "10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
                      📍 <strong>{m.site_name || "—"}</strong>
                    </p>
                    <p style={{ margin: "2px 0 0", fontSize: "11px", color: "#94a3b8" }}>{m.client_name || "—"}</p>
                  </div>
                  {m.location_description && (
                    <span style={{ fontSize: "11px", color: "#94a3b8", background: "#f8fafc", padding: "2px 8px", borderRadius: "6px", maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {m.location_description}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InfoPill({ icon, label, value }) {
  return (
    <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "7px 10px" }}>
      <p style={{ margin: 0, fontSize: "10px", color: "#94a3b8", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.05em" }}>{icon} {label}</p>
      <p style={{ margin: "3px 0 0", fontSize: "12px", fontWeight: "600", color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</p>
    </div>
  );
}
